/**
 * useTemplateTransform.ts — drives the TEMPLATE-BASED slide transform.
 *
 * The sibling of `useSlideTransform` (freehand AI design). Two user-driven
 * steps, so two entry points rather than one:
 *
 *   match()  — classify the current slide and rank catalog templates against it
 *   apply()  — fit the slide's content into the templates the user picked and
 *              insert one new slide per pick
 *
 * They must be called as a pair and in that order: `match()` caches the source
 * slide's content inventory server-side under its `request_id`, and `apply()`
 * replays that id so the fit does not have to resend the original slide. The
 * server releases the cache when `apply()` returns, so a second apply needs a
 * fresh `match()` — `phase` enforces that by returning to `"picking"` only
 * while the cache is still live.
 *
 * Unlike the freehand mode this is single-source-slide by design: the user picks
 * up to three templates for ONE slide and gets one variant per pick to compare,
 * which is what the mockup's "Choose up to 3 templates" flow describes.
 *
 * Originals are never touched — each variant is inserted after the source
 * slide, which stays exactly as it was.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { config } from "../../../../../shared/config/environment";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { newRequestId } from "../../../utils/requestId";
import {
  DeckStructureError,
  listSlidePartPaths,
  readPartText,
  readSlideDimensions,
  unzipDeck,
} from "../services/pptxDeckParts";
import { getActivePresentationBuffer } from "../../../utils/officePptx";
import { getSelectedSlides, NoSlideSelectedError } from "../services/slideSelection";
import { applyMappingPlan } from "../services/mappingPlanApply";
import {
  buildTemplateSlidePackage,
  readTemplateSlide,
  type TemplateSlide,
} from "../services/templatePackage";
import { insertTransformedSlideAfter } from "../services/slideTransformWriteback";
import {
  fetchTemplateFile,
  retrieveTemplates,
  templateFileKey,
  transformIntoTemplates,
  type TemplateApiOptions,
  type TemplateCandidate,
  type TemplateFitMode,
  type TemplateTransformResult,
  type TemplateTransformSlideInput,
} from "../services/slideTransformTemplateApi";
import { getErrorMessage, logSlideTransformError } from "../services/slideTransformTelemetry";
import type { SlideDimensions } from "../services/slideTransformApi";

const REQUEST_ID_PREFIX = "slide-transform-tpl-";

/** The mockup's cap: at most three variants to compare in one pass. */
export const MAX_TEMPLATE_PICKS = 3;

export type TemplatePhase = "idle" | "matching" | "picking" | "applying" | "done" | "cancelled" | "error";

/** Outcome for one picked template. */
export interface TemplateApplyOutcome {
  readonly templateId: string;
  readonly status: "done" | "failed";
  readonly detail?: string;
}

export interface TemplateTransformState {
  readonly phase: TemplatePhase;
  readonly candidates: readonly TemplateCandidate[];
  /** 0-based index into `TEMPLATE_APPLY_STAGES` while `phase` is `"applying"`. */
  readonly applyStage: number;
  readonly outcomes: readonly TemplateApplyOutcome[];
  /** Set only when `phase` is `"error"` — a run-level failure. */
  readonly error?: string;
}

const IDLE_STATE: TemplateTransformState = {
  phase: "idle",
  candidates: [],
  applyStage: 0,
  outcomes: [],
};

const READING_DECK_MESSAGE = "Couldn't read the presentation.";
const MATCH_FAILED_MESSAGE = "Couldn't find templates that match this slide.";
const NO_CANDIDATES_MESSAGE =
  "No templates matched this slide. Try AI design instead, or browse all layouts.";
const APPLY_FAILED_MESSAGE = "Couldn't apply the template.";
const NO_PLAN_MESSAGE = "The server didn't return a plan for this template.";
const UNPLACEABLE_MESSAGE =
  "Your content couldn't be matched to this layout's placeholders, so the slide wasn't added.";

/** The source slide a match/apply pair is working on. */
interface SourceSlide {
  /** Host-stable slide id — the insertion point for every variant. */
  readonly slideId: string;
  readonly xml: string;
  readonly dimensions: SlideDimensions;
}

export interface UseTemplateTransformResult {
  readonly state: TemplateTransformState;
  /** Classify the current slide and rank templates. No-op while busy. */
  readonly match: () => Promise<void>;
  /** Fit + insert one slide per picked template. No-op unless `phase` is `"picking"`. */
  readonly apply: (templateIds: readonly string[], fitMode: TemplateFitMode) => Promise<void>;
  readonly cancel: () => void;
  readonly reset: () => void;
}

/** Read the first selected slide's XML and the deck's slide size. */
async function readSourceSlide(): Promise<SourceSlide> {
  const selected = await getSelectedSlides();
  const first = selected[0];
  if (!first) throw new NoSlideSelectedError("Select a slide to transform.");

  const files = unzipDeck(await getActivePresentationBuffer());
  const partPath = listSlidePartPaths(files)[first.index];
  const xml = partPath ? readPartText(files, partPath) : null;
  if (!xml) {
    throw new DeckStructureError("The selected slide could not be read from this presentation.");
  }

  return { slideId: first.id, xml, dimensions: readSlideDimensions(files) };
}

/**
 * Fill one template with its fitted content and insert it, reporting the
 * outcome rather than throwing.
 *
 * Extracted from `apply`'s loop so the batch logic and the per-variant logic
 * are readable separately. It swallows its own failures by design: one template
 * failing must not lose the others, so every path returns a row for the summary
 * and the loop simply collects them.
 */
async function insertVariant({
  templateId,
  template,
  result,
  slideId,
  setStage,
}: Readonly<{
  templateId: string;
  template: TemplateSlide;
  result: TemplateTransformResult | undefined;
  slideId: string;
  setStage: (stage: number) => void;
}>): Promise<TemplateApplyOutcome> {
  if (!result?.valid) {
    return { templateId, status: "failed", detail: result?.error ?? NO_PLAN_MESSAGE };
  }

  try {
    setStage(4);
    const { slideXml, applied, skipped } = applyMappingPlan(
      template.slideXml,
      result.placeholderAssignments,
      result.slots ?? [],
    );

    // The plan had content but none of it could be placed — every slot was
    // unresolvable. Inserting now would add the template with its own
    // placeholder text ("Lorem…", "Deck_Body_1") and report success, which is
    // worse than failing: the user is told their content was fitted when the
    // slide holds none of it.
    //
    // The way this happens in practice is a backend that predates the
    // slot -> shape map, so `slots` arrives empty and nothing resolves.
    if (applied.length === 0 && Object.keys(result.placeholderAssignments).length > 0) {
      logSlideTransformError(
        "slide-transform-template-unplaceable",
        `No slot resolved for ${templateId}: ${skipped.map((s) => s.reason).join("; ")}`,
      );
      return { templateId, status: "failed", detail: UNPLACEABLE_MESSAGE };
    }

    setStage(5);
    const base64 = buildTemplateSlidePackage(template, slideXml);

    setStage(6);
    await insertTransformedSlideAfter(slideId, base64);
    return { templateId, status: "done" };
  } catch (error) {
    const message = getErrorMessage(error, APPLY_FAILED_MESSAGE);
    logSlideTransformError("slide-transform-template-apply", message);
    return { templateId, status: "failed", detail: message };
  }
}

export function useTemplateTransform(): UseTemplateTransformResult {
  const http = useDeckorateHttp();
  const [state, setState] = useState<TemplateTransformState>(IDLE_STATE);

  const isRunningRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  // Held across the match -> apply pair: apply() must replay the SAME
  // request_id, and re-reading the deck between the two could pick up a
  // different slide if the user moved the selection.
  const requestIdRef = useRef<string | null>(null);
  const sourceRef = useRef<SourceSlide | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const safeSetState = useCallback(
    (update: (previous: TemplateTransformState) => TemplateTransformState) => {
      if (isMountedRef.current) setState(update);
    },
    [],
  );

  const apiOptions = useCallback(
    (signal: AbortSignal): TemplateApiOptions => ({
      baseUrl: config.deckorateApiBaseUrl,
      authorizedFetch: http.fetch,
      signal,
    }),
    [http],
  );

  /** Shared terminal handling for a thrown run — cancellation is not an error. */
  const settleFailure = useCallback(
    (controller: AbortController, error: unknown, fallback: string, reason: string): void => {
      if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) {
        safeSetState((previous) => ({ ...previous, phase: "cancelled" }));
        return;
      }
      const message = getErrorMessage(
        error,
        error instanceof NoSlideSelectedError ? error.message : fallback,
      );
      logSlideTransformError(reason, message);
      safeSetState((previous) => ({ ...previous, phase: "error", error: message }));
    },
    [safeSetState],
  );

  const match = useCallback(async (): Promise<void> => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;
    safeSetState(() => ({ ...IDLE_STATE, phase: "matching" }));

    // The deck read and the ranking call fail for unrelated reasons, so they
    // report differently: "couldn't read the presentation" vs "no templates
    // matched". Tracked here so the shared handler below picks the right one.
    let readingDeck = true;

    try {
      const source = await readSourceSlide();
      sourceRef.current = source;
      readingDeck = false;

      const requestId = newRequestId(REQUEST_ID_PREFIX);
      requestIdRef.current = requestId;

      const { candidates } = await retrieveTemplates(
        {
          request_id: requestId,
          _xml: source.xml,
          slide_dimensions: source.dimensions,
          top_n: 12,
        },
        apiOptions(controller.signal),
      );

      if (controller.signal.aborted) {
        safeSetState((previous) => ({ ...previous, phase: "cancelled" }));
        return;
      }

      if (candidates.length === 0) {
        // Nothing to show a picker for, so this is a dead end rather than a
        // "picking" state with an empty grid.
        safeSetState((previous) => ({ ...previous, phase: "error", error: NO_CANDIDATES_MESSAGE }));
        return;
      }

      safeSetState((previous) => ({ ...previous, phase: "picking", candidates }));
    } catch (error) {
      settleFailure(
        controller,
        error,
        readingDeck ? READING_DECK_MESSAGE : MATCH_FAILED_MESSAGE,
        "slide-transform-template-match",
      );
    } finally {
      isRunningRef.current = false;
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [apiOptions, safeSetState, settleFailure]);

  const apply = useCallback(
    async (templateIds: readonly string[], fitMode: TemplateFitMode): Promise<void> => {
      if (isRunningRef.current) return;
      const requestId = requestIdRef.current;
      const source = sourceRef.current;
      const picks = templateIds.slice(0, MAX_TEMPLATE_PICKS);
      if (!requestId || !source || picks.length === 0) return;

      isRunningRef.current = true;
      const controller = new AbortController();
      abortRef.current = controller;

      const setStage = (applyStage: number) =>
        safeSetState((previous) => ({ ...previous, phase: "applying", applyStage }));
      const abortedNow = () => controller.signal.aborted;

      safeSetState((previous) => ({ ...previous, phase: "applying", applyStage: 0, outcomes: [] }));

      try {
        const candidateById = new Map(state.candidates.map((c) => [c.id, c] as const));

        // ── Fetch every picked template (stage 1) ──
        // One `.pptx` holds many layouts (a catalog id is "<file>#<slide>") and
        // the files are tens of MB, so fetch each DISTINCT file once and reuse
        // its bytes for every pick taken from it. Three picks from one file
        // would otherwise download the same ~24 MB three times.
        setStage(1);
        const firstIdByFile = new Map<string, string>();
        for (const id of picks) {
          const key = templateFileKey(id);
          if (!firstIdByFile.has(key)) firstIdByFile.set(key, id);
        }
        const fileEntries = [...firstIdByFile];
        const fetched = await Promise.all(
          fileEntries.map(([, id]) => fetchTemplateFile(id, apiOptions(controller.signal))),
        );
        const bytesByFile = new Map(
          fileEntries.map(([key], index) => [key, fetched[index]!] as const),
        );
        // Safe to share one buffer: readTemplateSlide unzips per call and
        // buildSingleSlidePackage shallow-copies, so neither mutates the bytes.
        const files = picks.map((id) => bytesByFile.get(templateFileKey(id))!);
        if (abortedNow()) {
          safeSetState((previous) => ({ ...previous, phase: "cancelled" }));
          return;
        }

        // ── Parse each package (stage 2) ──
        setStage(2);
        const templates = picks.map((id, index) =>
          readTemplateSlide(files[index]!, candidateById.get(id)?.slideIndex ?? null),
        );

        // ── One fit call for the whole batch (stage 3) ──
        setStage(3);
        const slides: TemplateTransformSlideInput[] = picks.map((id, index) => ({
          slide_index: index,
          _xml: templates[index]!.slideXml,
          slide_dimensions: templates[index]!.slideDimensions,
          template_id: id,
          fit_mode: fitMode,
        }));

        const { results } = await transformIntoTemplates(
          { request_id: requestId, slides },
          apiOptions(controller.signal),
        );
        if (abortedNow()) {
          safeSetState((previous) => ({ ...previous, phase: "cancelled" }));
          return;
        }

        // The cache is consumed by the call above, so a retry needs a new match.
        requestIdRef.current = null;

        const resultByIndex = new Map<number, TemplateTransformResult>(
          results.map((result) => [result.slide_index, result] as const),
        );

        // ── Fill, package and insert each variant (stages 4-6) ──
        const outcomes: TemplateApplyOutcome[] = [];
        // Reverse order: each variant is inserted directly after the source
        // slide, so inserting the last pick first leaves them in pick order.
        for (let index = picks.length - 1; index >= 0; index -= 1) {
          outcomes.push(
            await insertVariant({
              templateId: picks[index]!,
              template: templates[index]!,
              result: resultByIndex.get(index),
              slideId: source.slideId,
              setStage,
            }),
          );
        }

        outcomes.reverse();
        const anyApplied = outcomes.some((outcome) => outcome.status === "done");
        safeSetState((previous) => ({
          ...previous,
          outcomes,
          phase: anyApplied ? "done" : "error",
          ...(anyApplied ? {} : { error: APPLY_FAILED_MESSAGE }),
        }));
      } catch (error) {
        settleFailure(controller, error, APPLY_FAILED_MESSAGE, "slide-transform-template-run");
      } finally {
        isRunningRef.current = false;
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [apiOptions, safeSetState, settleFailure, state.candidates],
  );

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    requestIdRef.current = null;
    sourceRef.current = null;
    safeSetState(() => IDLE_STATE);
  }, [safeSetState]);

  return { state, match, apply, cancel, reset };
}
